export type Category =
  | "korkort"
  | "alkohol"
  | "sjofolk"
  | "arbete"
  | "resor"
  | "forsakring"
  | "sport"
  | "adoption"
  | "afs"
  | "ovriga";

export type Mode = "digital" | "onsite";

export interface Certificate {
  slug: string;
  category: Category;
  price: { sv: string; en: string };
  modes: Mode[];
  title: { sv: string; en: string };
  description: { sv: string; en: string };
}

/** Titles, prices and intros aligned with intygslakaren-v2 service catalog. */
export const certificates: Certificate[] = [
  {
    slug: "hogre-behorighet",
    category: "korkort",
    price: { sv: "Från 995 kr", en: "From 995 SEK" },
    modes: ["digital", "onsite"],
    title: {
      sv: "Läkarintyg för högre körkortsbehörighet",
      en: "Medical certificate for a higher driving licence",
    },
    description: {
      sv: "För lastbil, buss eller båda behörigheterna. Välj videobesök eller ett fysiskt besök i Göteborg.",
      en: "Medical certificates for Swedish truck and bus licence categories. Choose a clinic appointment in Gothenburg or a video consultation when suitable.",
    },
  },
  {
    slug: "c-ce",
    category: "korkort",
    price: { sv: "Från 995 kr", en: "From 995 SEK" },
    modes: ["digital", "onsite"],
    title: {
      sv: "Läkarintyg för C och CE (lastbil)",
      en: "Truck driving medical – C and CE",
    },
    description: {
      sv: "För lastbil: C1, C1E, C och CE. Välj videobesök eller ett fysiskt besök i Göteborg.",
      en: "For C1, C1E, C and CE truck licence categories. Book a medical assessment at our Gothenburg clinic or by video when suitable.",
    },
  },
  {
    slug: "d-de",
    category: "korkort",
    price: { sv: "Från 995 kr", en: "From 995 SEK" },
    modes: ["digital", "onsite"],
    title: {
      sv: "Läkarintyg för D och DE (buss)",
      en: "Bus driving medical – D and DE",
    },
    description: {
      sv: "För buss: D1, D1E, D och DE. Välj videobesök eller ett fysiskt besök i Göteborg. Hörselprövning behöver också planeras.",
      en: "For D1, D1E, D and DE bus licence categories. Choose a clinic or video appointment. The required hearing assessment also needs to be arranged.",
    },
  },
  {
    slug: "buss-lastbil",
    category: "korkort",
    price: { sv: "Från 995 kr", en: "From 995 SEK" },
    modes: ["digital", "onsite"],
    title: {
      sv: "Läkarintyg för buss och lastbil",
      en: "Medical certificate for bus and truck licences",
    },
    description: {
      sv: "För dig som behöver intyg för både buss och lastbil. Välj videobesök eller ett fysiskt besök i Göteborg.",
      en: "One medical assessment covering both bus and truck licence categories. Tell us all the categories you are applying for.",
    },
  },
  {
    slug: "taxi",
    category: "korkort",
    price: { sv: "Från 995 kr", en: "From 995 SEK" },
    modes: ["digital", "onsite"],
    title: {
      sv: "Läkarintyg för taxiförarlegitimation",
      en: "Medical certificate for a taxi driver licence",
    },
    description: {
      sv: "Läkarintyg för taxiförarlegitimation. Välj videobesök eller ett fysiskt besök i Göteborg.",
      en: "A medical certificate for a Swedish taxi driver licence. Choose a video appointment or an in-person visit in Gothenburg.",
    },
  },
  {
    slug: "alkolas",
    category: "alkohol",
    price: { sv: "2 995 kr inkl. prover", en: "2,995 SEK including tests" },
    modes: ["digital", "onsite"],
    title: { sv: "Läkarintyg för alkolås", en: "Medical certificate for an alcohol interlock" },
    description: {
      sv: "För dig som ska ansöka om alkolås. Bedömning av legitimerad läkare, prover och läkarintyg ingår i priset.",
      en: "Help with the medical part of a Swedish alcohol interlock application: registration, a testing plan, ordinary samples and the medical certificate for 2,995 SEK.",
    },
  },
  {
    slug: "droger-alkohol",
    category: "alkohol",
    price: { sv: "Från 1 995 kr", en: "From 1,995 SEK" },
    modes: ["digital", "onsite"],
    title: {
      sv: "Läkarintyg för alkohol, narkotika och läkemedel",
      en: "Alcohol, drug and medication certificates",
    },
    description: {
      sv: "Prover och läkarintyg till Transportstyrelsen. Inskrivning kostar 1 995 kr inklusive första provtagningen. Avslutningsbesök och intyg kostar 1 995 kr. Fortsatt provtagning tillkommer.",
      en: "Testing and medical certificates for the Swedish Transport Agency. Registration costs 1,995 SEK, including the first sampling occasion. The final appointment and certificate cost 1,995 SEK. Further sampling is charged separately.",
    },
  },
  {
    slug: "salivtest",
    category: "alkohol",
    price: { sv: "1 500 kr", en: "1,500 SEK" },
    modes: ["onsite"],
    title: { sv: "Salivtest för drogtestning", en: "Saliva drug test" },
    description: {
      sv: "Privat salivtest i Göteborg. I paketet ingår amfetamin, benso, THC, kokain, ecstasy och opiater. Analys i samarbete med Unilabs.",
      en: "Private saliva drug test in Gothenburg. The package includes amphetamine, benzodiazepines, THC, cocaine, ecstasy and opiates. Laboratory analysis with Unilabs.",
    },
  },
  {
    slug: "urinprov",
    category: "alkohol",
    price: { sv: "1 500 kr", en: "1,500 SEK" },
    modes: ["onsite"],
    title: { sv: "Urinprov för drogtestning", en: "Urine drug test" },
    description: {
      sv: "Privat urinprov i Göteborg. I paketet ingår opiater, kokain, amfetamin och THC. Analys i samarbete med Unilabs.",
      en: "Private urine drug test in Gothenburg. The package includes opiates, cocaine, amphetamine and THC. Laboratory analysis with Unilabs.",
    },
  },
  {
    // Legacy slug kept for URLs; content matches v2 korkort-ovriga-tillstand (ADHD page redirects there).
    slug: "korkort-adhd",
    category: "korkort",
    price: { sv: "Pris efter genomgång", en: "Price after review" },
    modes: ["digital", "onsite"],
    title: {
      sv: "Övriga tillstånd – läkarintyg för körkort",
      en: "Driving licences and other medical conditions",
    },
    description: {
      sv: "Behöver du ett körkortsintyg för ett annat medicinskt tillstånd, eller är du osäker på vilket intyg du behöver? Skicka din blankett, ditt befintliga intyg eller brevet från Transportstyrelsen så ser vi om vi kan hjälpa dig.",
      en: "Need a driving licence certificate for another medical condition? Send the requested form or the Swedish Transport Agency’s letter so we can assess whether we can help.",
    },
  },
  {
    slug: "korkort-diabetes",
    category: "korkort",
    price: { sv: "1 995 kr", en: "1,995 SEK" },
    modes: ["digital", "onsite"],
    title: { sv: "Körkortsintyg vid diabetes", en: "Driving licence medical certificate for diabetes" },
    description: {
      sv: "Läkarintyg vid diabetes för ditt körkort till fast pris: 1 995 kr. Boka din tid direkt och välj videobesök eller besök på mottagningen i Göteborg.",
      en: "A driving licence medical assessment for diabetes at a fixed price of 1,995 SEK. Book a video consultation or visit our Gothenburg clinic.",
    },
  },
  {
    slug: "korkort-hjarta",
    category: "korkort",
    price: { sv: "1 995 kr", en: "1,995 SEK" },
    modes: ["digital", "onsite"],
    title: {
      sv: "Körkortsintyg vid hjärt- och kärlsjukdom",
      en: "Driving licence medical certificate for heart disease",
    },
    description: {
      sv: "Läkarintyg vid hjärt- och kärlsjukdom för ditt körkort till fast pris: 1 995 kr. Boka din tid direkt och välj videobesök eller besök på mottagningen i Göteborg.",
      en: "A driving licence medical assessment for cardiovascular conditions at a fixed price of 1,995 SEK. Book by video or at our Gothenburg clinic.",
    },
  },
  {
    slug: "offshore",
    category: "sjofolk",
    price: { sv: "2 500 kr", en: "2,500 SEK" },
    modes: ["onsite"],
    title: { sv: "Offshore Norge", en: "Norwegian offshore medical in Gothenburg" },
    description: {
      sv: "Norskt offshoreintyg vid fysiskt besök i Göteborg. Registrerade hos Statsforvaltaren i Rogaland. Skicka en förfrågan så planerar vi besöket.",
      en: "A Norwegian offshore medical at our Gothenburg clinic for 2,500 SEK. We are registered with Statsforvaltaren in Rogaland. Send an enquiry to arrange your visit.",
    },
  },
  {
    slug: "oguk",
    category: "sjofolk",
    price: { sv: "5 000 kr", en: "5,000 SEK" },
    modes: ["onsite"],
    title: { sv: "OEUK offshore medical", en: "OEUK offshore medical in Gothenburg" },
    description: {
      sv: "Godkända enligt OEUK. Fysisk undersökning i Göteborg. Skicka en kontaktförfrågan så återkommer vi för att planera besöket.",
      en: "An OEUK offshore medical at our Gothenburg clinic for 5,000 SEK. We are approved under OEUK. Contact us to plan an in-person examination.",
    },
  },
  {
    slug: "sjofolksintyg",
    category: "sjofolk",
    price: { sv: "1 100 kr", en: "1,100 SEK" },
    modes: ["onsite"],
    title: { sv: "Sjöfolksintyg – Manila/STCW", en: "Seafarer medical certificate – Manila/STCW" },
    description: {
      sv: "Manila/STCW-intyg i Göteborg. Hörseltest vid första ansökan: + 395 kr när det behövs. Intyg efter besöket om inga extra åtgärder krävs.",
      en: "A Swedish seafarer medical under STCW with the Manila amendments, at our Gothenburg clinic. Examination and certificate: 1,100 SEK. Hearing test +395 SEK when needed.",
    },
  },
  {
    slug: "au-pair",
    category: "arbete",
    price: { sv: "Från 1 500 kr", en: "From 1,500 SEK" },
    modes: ["digital", "onsite"],
    title: { sv: "Läkarintyg för au pair", en: "Au pair medical certificate" },
    description: {
      sv: "Hälsointyg inför din au pair-vistelse. Boka läkarbesöket online eller på mottagningen i Göteborg. Om intyget kräver prover eller röntgen kan vi ordna det i hela Sverige.",
      en: "A health certificate for your au pair placement. Book by video or at our Gothenburg clinic. Tests and X-rays can be arranged across Sweden if required.",
    },
  },
  {
    slug: "forstadagsintyg",
    category: "arbete",
    price: { sv: "1 000 kr", en: "1,000 SEK" },
    modes: ["digital", "onsite"],
    title: { sv: "Förstadagsintyg", en: "First-day sickness certificate" },
    description: {
      sv: "Behöver din arbetsgivare ett läkarintyg från första sjukdagen? Boka en läkarbedömning online eller på mottagningen i Göteborg. Priset är 1 000 kr.",
      en: "Does your employer require a certificate from your first day of sickness? Book a medical assessment by video or in Gothenburg for 1,000 SEK.",
    },
  },
  {
    slug: "utlandsstudier",
    category: "arbete",
    price: { sv: "Från 1 599 kr", en: "From 1,599 SEK" },
    modes: ["digital", "onsite"],
    title: {
      sv: "Läkarintyg för utlandsstudier eller arbete",
      en: "Medical certificate for study or work abroad",
    },
    description: {
      sv: "Läkarintyg inför studier eller arbete utomlands, anpassat efter mottagarens blankett. Läkarbesöket kan göras online. Vi kan ordna provtagning och röntgen i hela Sverige.",
      en: "A certificate based on the form required by your university, employer or other recipient. Video appointments are available when suitable; tests and X-rays can be arranged across Sweden.",
    },
  },
  {
    slug: "nattarbete",
    category: "arbete",
    price: { sv: "2 000 kr", en: "2,000 SEK" },
    modes: ["onsite"],
    title: { sv: "Medicinsk kontroll vid nattarbete", en: "Night work medical examination" },
    description: {
      sv: "Medicinsk kontroll för dig som arbetar natt. Läkarbesök, blodtryckskontroll och provtagning ingår i kontrollens upplägg. Besöket görs enbart fysiskt på vår mottagning i Göteborg.",
      en: "A medical examination focusing on health, working hours, sleep and recovery. Book directly for an in-person appointment in Gothenburg. Price: 2,000 SEK.",
    },
  },
  {
    slug: "fit-for-fly",
    category: "resor",
    price: { sv: "1 500 kr", en: "1,500 SEK" },
    modes: ["digital", "onsite"],
    title: { sv: "Fit-to-fly och friskintyg", en: "Fit-to-fly and health certificates" },
    description: {
      sv: "Bedömning när flygbolag eller annan mottagare efterfrågar medicinskt intyg.",
      en: "A medical assessment when an airline or other recipient asks for a health certificate before travel. Price: 1,500 SEK.",
    },
  },
  {
    slug: "installd-resa",
    category: "resor",
    price: { sv: "1 000 kr", en: "1,000 SEK" },
    modes: ["digital", "onsite"],
    title: { sv: "Läkarintyg vid inställd resa", en: "Medical certificate for travel cancellation" },
    description: {
      sv: "Bedömning när sjukdom påverkar en planerad resa och försäkringsbolaget behöver intyg. Läkarbedömning och intyg kostar 1 000 kr. Boka din tid direkt online.",
      en: "A medical assessment when illness affects a planned trip and your insurer requires a certificate. Consultation and certificate: 1,000 SEK. Book directly online.",
    },
  },
  {
    slug: "reseintyg-gravid",
    category: "resor",
    price: { sv: "1 600 kr", en: "1,600 SEK" },
    modes: ["digital", "onsite"],
    title: { sv: "Reseintyg vid graviditet", en: "Travel certificate during pregnancy" },
    description: {
      sv: "Bedömning inför resa under graviditet när flygbolaget kräver intyg. Välj videobesök eller ett fysiskt besök i Göteborg.",
      en: "A medical assessment when your airline requires a certificate for travel during pregnancy. Choose a video appointment or an in-person visit in Gothenburg. Price: 1,600 SEK.",
    },
  },
  {
    slug: "intyg-forsakring",
    category: "forsakring",
    price: { sv: "3 000 kr", en: "3,000 SEK" },
    modes: ["onsite"],
    title: {
      sv: "Försäkringsintyg för tjänstepension",
      en: "Medical certificate for occupational pension insurance",
    },
    description: {
      sv: "För dig som behöver läkarintyg för att teckna, förlänga, flytta eller avsluta din tjänstepension. Bedömningen görs vid ett fysiskt besök i Göteborg. Boka din tid direkt online.",
      en: "For taking out, extending, transferring or ending occupational pension insurance when a medical certificate is required. In-person appointment in Gothenburg: 3,000 SEK.",
    },
  },
  {
    slug: "invaliditetsintyg",
    category: "forsakring",
    price: { sv: "Ingår i försäkringen", en: "Included in insurance" },
    modes: ["onsite"],
    title: { sv: "Invaliditetsintyg", en: "Disability certificate" },
    description: {
      sv: "Försäkringsmedicinskt utlåtande vid bedömning av bestående funktionsnedsättning. Bedömningen görs vid ett fysiskt besök i Göteborg. Boka din tid direkt online.",
      en: "An insurance medical report to support assessment of lasting functional impairment. The examination takes place at our Gothenburg clinic.",
    },
  },
  {
    slug: "fallskarmsintyg",
    category: "sport",
    price: { sv: "1 800 kr", en: "1,800 SEK" },
    modes: ["onsite"],
    title: { sv: "Läkarintyg för fallskärmshoppning", en: "Skydiving medical certificate" },
    description: {
      sv: "Ska du gå en fallskärmskurs eller har klubben bett om ett läkarintyg? Boka en fysisk läkarundersökning i Göteborg. Urinsticka och blodvärde (Hb) ingår i priset.",
      en: "An in-person medical examination in Gothenburg for skydiving. The price is 1,800 SEK, including a urine dipstick and haemoglobin (Hb) test.",
    },
  },
  {
    slug: "tavlingsintyg",
    category: "sport",
    price: { sv: "Från 1 100 kr", en: "From 1,100 SEK" },
    modes: ["digital", "onsite"],
    title: { sv: "Tävlingsintyg för sport och idrott", en: "Sports and competition medical certificate" },
    description: {
      sv: "Behöver du ett läkarintyg inför ett lopp, en tävling eller organiserad idrott? Vi anpassar bedömningen efter arrangörens krav. Välj ett fysiskt besök i Göteborg eller video när intyget kan bedömas digitalt.",
      en: "A medical certificate for a race, competition or organised sport, based on the organiser’s requirements. Choose Gothenburg or a video consultation when suitable.",
    },
  },
  {
    slug: "adoptionsintyg",
    category: "adoption",
    price: { sv: "2 600 kr", en: "2,600 SEK" },
    modes: ["onsite"],
    title: { sv: "Läkarintyg för adoption", en: "Adoption medical certificate" },
    description: {
      sv: "Hälsointyg inför adoption på vår mottagning i Göteborg. Läkarundersökning, provtagning och intyg ingår i priset. Ta med hälsodeklarationen, aktuell blankett och instruktioner från familjerätten eller adoptionsorganisationen.",
      en: "A health certificate for adoption at our Gothenburg clinic. The price is 2,600 SEK including the medical examination, sampling for the adoption certificate and the certificate.",
    },
  },
  {
    slug: "afs-allergi-kemiska",
    category: "afs",
    price: { sv: "Från 3 000 kr", en: "From 3,000 SEK" },
    modes: ["onsite"],
    title: {
      sv: "Medicinsk kontroll vid allergiframkallande ämnen",
      en: "Medical examination for sensitising chemicals",
    },
    description: {
      sv: "Olika kemiska produkter ställer olika krav på medicinsk kontroll. Skicka en förfrågan så går vi igenom exponeringen och planerar rätt undersökning med er.",
      en: "Requirements differ between chemical products. Send an enquiry so we can review the exposure and plan the appropriate occupational examination.",
    },
  },
  {
    slug: "afs-asbest",
    category: "afs",
    price: { sv: "4 000 kr", en: "4,000 SEK" },
    modes: ["onsite"],
    title: {
      sv: "Medicinsk kontroll vid asbest, kvarts och fibrer",
      en: "Medical examination for asbestos, silica and fibres",
    },
    description: {
      sv: "Kontroller vid fibrosframkallande damm behöver planeras utifrån ämne och exponering. Skicka formuläret så samordnar vi underlag, undersökningar och besök med er.",
      en: "Examinations for exposure to fibrogenic dust need to be planned around the substance and exposure. Send an enquiry so we can coordinate records, examinations and appointments.",
    },
  },
  {
    slug: "afs-bly",
    category: "afs",
    price: { sv: "4 000 kr", en: "4,000 SEK" },
    modes: ["onsite"],
    title: { sv: "Medicinsk kontroll vid blyarbete", en: "Medical examination for lead exposure" },
    description: {
      sv: "Vid blyexponering behöver läkarbedömning och provtagning samordnas. Skicka en förfrågan så planerar vi kontrollen och eventuell fortsatt provtagning med er.",
      en: "Lead exposure requires coordinated medical assessment and blood testing. Send an enquiry to plan the examination and continued monitoring.",
    },
  },
  {
    slug: "afs-handintensivt",
    category: "afs",
    price: { sv: "2 000 kr", en: "2,000 SEK" },
    modes: ["onsite"],
    title: {
      sv: "Medicinsk kontroll vid handintensivt arbete",
      en: "Medical examination for hand-intensive work",
    },
    description: {
      sv: "Arbetar du med snabba, upprepade handledsrörelser som kräver kraft? Boka en medicinsk kontroll direkt på vår mottagning i Göteborg.",
      en: "Do your tasks involve rapid, repeated wrist movements requiring force? Book a medical examination directly at our Gothenburg clinic. Price: 2,000 SEK.",
    },
  },
  {
    slug: "afs-klattring",
    category: "afs",
    price: { sv: "Från 4 000 kr", en: "From 4,000 SEK" },
    modes: ["onsite"],
    title: {
      sv: "Medicinsk kontroll vid klättring med stor nivåskillnad",
      en: "Medical examination for climbing at height",
    },
    description: {
      sv: "Behöver ni en kontroll inför klättring i mast, stolpe, träd eller på stege? Skicka en förfrågan så går vi igenom arbetskraven och planerar undersökningen.",
      en: "Need an examination before climbing masts, poles, trees or ladders? Send an enquiry so we can review the tasks and plan the required assessment.",
    },
  },
  {
    // Legacy separate page; v2 folds this into afs-klattring.
    slug: "afs-mast-stolpe",
    category: "afs",
    price: { sv: "Från 4 000 kr", en: "From 4,000 SEK" },
    modes: ["onsite"],
    title: {
      sv: "Medicinsk kontroll vid mast- och stolparbete",
      en: "Medical examination for mast and pole work",
    },
    description: {
      sv: "Behöver ni en kontroll inför klättring i mast, stolpe, träd eller på stege? Skicka en förfrågan så går vi igenom arbetskraven och planerar undersökningen.",
      en: "Need an examination before climbing masts, poles, trees or ladders? Send an enquiry so we can review the tasks and plan the required assessment.",
    },
  },
  {
    slug: "afs-vibrationer",
    category: "afs",
    price: { sv: "2 000 kr", en: "2,000 SEK" },
    modes: ["onsite"],
    title: {
      sv: "Medicinsk kontroll vid vibrationsexponering",
      en: "Medical examination for vibration exposure",
    },
    description: {
      sv: "Använder du vibrerande verktyg i arbetet? Boka din vibrationskontroll direkt på mottagningen i Göteborg.",
      en: "Do you use vibrating tools at work? Book an in-person vibration medical examination directly in Gothenburg. Price: 2,000 SEK.",
    },
  },
  {
    // Legacy separate page; v2 merges into afs-allergiframkallande.
    slug: "afs-allergi-produkter",
    category: "afs",
    price: { sv: "Från 3 000 kr", en: "From 3,000 SEK" },
    modes: ["onsite"],
    title: {
      sv: "Medicinsk kontroll vid allergiframkallande produkter",
      en: "Medical examination for sensitising products",
    },
    description: {
      sv: "Olika kemiska produkter ställer olika krav på medicinsk kontroll. Skicka en förfrågan så går vi igenom exponeringen och planerar rätt undersökning med er.",
      en: "Requirements differ between chemical products. Send an enquiry so we can review the exposure and plan the appropriate occupational examination.",
    },
  },
  {
    slug: "ovriga",
    category: "ovriga",
    price: { sv: "Kostnadsförslag", en: "Quote on request" },
    modes: ["digital", "onsite"],
    title: { sv: "Övriga intyg", en: "Other certificates" },
    description: {
      sv: "Behöver du ett läkarintyg som inte finns i listan? Kontakta oss så hjälper vi dig vidare.",
      en: "Need a medical certificate that is not listed? Contact us and we will help you find the right next step.",
    },
  },
];

export const reviews = [
  { name: "Dave Spray", date: "2024-07-14", text: { sv: "Möjligen det bästa läkarbesök jag någonsin haft! Mycket professionellt och stressfritt.", en: "Possibly the best doctor's visit I've ever had! Very professional and such an easy stress free visit!" } },
  { name: "Christopher Lindkvist", date: "2024-11-26", text: { sv: "Mycket trevliga och professionella läkare. Snabbt och smidigt att boka och undersökningen lika smidig. Högsta betyg!", en: "Very pleasant and professional doctors. Booking was quick and smooth and so was the examination. Top marks!" } },
  { name: "eCko", date: "2024-10-14", text: { sv: "Jag besökte Intygsläkaren i Göteborg och blev mycket nöjd. Kompetent, trevlig och socialt bemötande. Kan varmt rekommendera.", en: "I visited Intygsläkaren in Gothenburg and was very satisfied. Competent, friendly and social. Highly recommended." } },
  { name: "Ebba Hammarstedt", date: "2024-06-14", text: { sv: "Väldigt bra och proffsig service! Läkaren Toba gav ett fantastiskt bemötande. Rekommenderar starkt!", en: "Really good and professional service! Dr. Toba was fantastic. Highly recommended!" } },
  { name: "Christian Hansson", date: "2024-08-14", text: { sv: "Kanon! Snabbt och smidigt, massor med lediga tider och inga väntetider!", en: "Excellent! Quick, smooth, plenty of slots and no waiting times!" } },
  { name: "Bengt Gustavsson", date: "2024-10-14", text: { sv: "Ett vänligt och professionellt bemötande, på en skala 1–10 får Intygsläkaren 12 poäng!", en: "Friendly and professional service – on a 1–10 scale Intygsläkaren gets 12 points!" } },
];
